import classNames from 'classnames'
import { navigate } from 'gatsby'
import React, { useCallback, useEffect, useState } from 'react'
import { useDrawer } from '../../../hooks/useDrawer'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { Grid } from '../../Grid'
import { Paragraph } from '../../Paragraph'
import { Form, FormProps } from '../../ReactHookForm/Form'
import { Spinner } from '../../Spinner'
import { ResultMessageBox } from './ResultMessageBox'

export interface ContactFormProps {
  errorMessageBody?: string
  errorMessageTitle?: string
  formProps?: Omit<FormProps, 'onSubmitting' | 'onSubmitted'>
  id?: string
  preamble?: string
  successMessageBody?: string
  successMessageTitle?: string
  successRedirectUrl?: string
  successRedirectFileUrl?: string
  title?: string
}

export const ContactForm = ({
  errorMessageBody,
  errorMessageTitle,
  formProps,
  id,
  preamble,
  successMessageBody,
  successMessageTitle,
  successRedirectUrl,
  successRedirectFileUrl,
  title,
}: ContactFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [success, setSuccess] = useState(false)

  const { containerRef, contentRef, updateHeight } = useDrawer<
    HTMLDivElement,
    HTMLDivElement
  >({ defaultClosed: true })

  const onSubmitting = useCallback(() => {
    setIsSubmitting(true)
    setHasSubmitted(false)
  }, [])

  const onSubmitted = useCallback((success: boolean) => {
    setIsSubmitting(false)
    setHasSubmitted(true)
    setSuccess(success)
  }, [])

  useEffect(() => {
    if (success && hasSubmitted) {
      if (!isNullOrWhiteSpace(successRedirectUrl)) {
        navigate(successRedirectUrl)
      } else if (!isNullOrWhiteSpace(successRedirectFileUrl) && !!window) {
        window.open(successRedirectFileUrl, '_blank')
      }
    }
  }, [hasSubmitted, success, successRedirectFileUrl, successRedirectUrl])

  useEffect(() => {
    if (isSubmitting || hasSubmitted) {
      updateHeight()
    }

    if (hasSubmitted && contentRef.current) {
      contentRef.current.scrollIntoView()
    }
  }, [contentRef, isSubmitting, hasSubmitted, updateHeight])

  const successMessageLength =
    (successMessageTitle?.length ?? 0) + (successMessageBody?.length ?? 0)

  const errorMessageLength =
    (errorMessageTitle?.length ?? 0) + (errorMessageBody?.length ?? 0)

  // Not 100% accurate but good enough: Figure out which message has the tallest height depending on its
  // character length. Display that message statically for correct minimum height calculations, and
  // display the other message with absolute positioning.
  const successMessageBoxIsLongest = successMessageLength > errorMessageLength

  return (
    <Grid
      additionalClassName={classNames('py-30 transition duration-500 sm:py-60 lg:py-90', {
        'bg-primary': !hasSubmitted,
        'bg-success': hasSubmitted && success,
        'bg-error': hasSubmitted && !success,
      })}
    >
      <div
        className={classNames(
          'col-span-full flex flex-col gap-30 mb-30',
          'sm:col-start-1 sm:col-span-3 sm:ml-120 sm:mb-0',
          'md:ml-0 md:mr-90',
          'lg:col-start-2 lg:col-span-3',
          'xl:col-start-2 xl:col-span-3 xl:-ml-45 xl:mr-45',
          'xxl:col-start-4 xxl:col-span-3'
        )}
      >
        {!isNullOrWhiteSpace(title) && (
          <h2 className="font-h2 text-h2 text-shade-lightest-warm">{title}</h2>
        )}

        {!isNullOrWhiteSpace(preamble) && (
          <Paragraph className="font-body text-body text-neutral">{preamble}</Paragraph>
        )}

        {/* Results in sm and above */}
        <div
          className={classNames(
            'relative hidden self-center mt-auto transition-all duration-500 transform sm:block',
            {
              '-translate-x-[calc(200%)]': !isSubmitting && !hasSubmitted,
              '-translate-x-[calc(100%)]': isSubmitting,
            }
          )}
        >
          <div
            className={classNames({
              'absolute bottom-0 left-0`': !successMessageBoxIsLongest,
            })}
          >
            <ResultMessageBox
              messageBody={successMessageBody}
              messageTitle={successMessageTitle}
              success={true}
              visible={hasSubmitted && success}
            />
          </div>

          <div
            className={classNames({
              'absolute bottom-0 left-0`': successMessageBoxIsLongest,
            })}
          >
            <ResultMessageBox
              messageBody={errorMessageBody}
              messageTitle={errorMessageTitle}
              success={false}
              visible={hasSubmitted && !success}
            />
          </div>

          <div
            className={classNames(
              'absolute top-1/2 left-[150%] transform -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500 pointer-events-none',
              {
                'opacity-0': !isSubmitting,
                'opacity-100': isSubmitting,
              }
            )}
          >
            <Spinner size="large" />
          </div>
        </div>
      </div>

      <div
        className={classNames(
          'col-span-full',
          'sm:col-start-4 sm:col-span-3 sm:mr-120',
          'md:mr-0',
          'lg:col-start-5 lg:col-span-3',
          'xl:col-start-6 xl:col-span-4 xl:ml-75 xl:-mr-75',
          'xxl:col-start-8 xxl:col-span-4'
        )}
      >
        {!!formProps && (
          <div className="p-30 rounded-30 bg-neutral tb:p-75 sm:p-30 md:p-75">
            <Form
              id={id}
              name={title}
              {...formProps}
              minimumSubmissionTimeMs={2000}
              onSubmitting={onSubmitting}
              onSubmitted={onSubmitted}
              downloadFileOnSubmit={!isNullOrWhiteSpace(successRedirectFileUrl)}
            />
          </div>
        )}
      </div>

      {/* Results in mobile and tb */}
      <div className="col-span-full sm:hidden">
        <div className="transition-all duration-500 h-0" ref={containerRef}>
          <div className="pt-30" ref={contentRef}>
            <div className="relative flex flex-col">
              <div
                className={classNames('self-center transition-opacity duration-500', {
                  'opacity-0': !isSubmitting,
                  'opacity-100': isSubmitting,
                  'absolute top-0 left-1/2 transform -translate-x-1/2': hasSubmitted,
                })}
              >
                <Spinner />
              </div>

              <div
                className={classNames({
                  'absolute inset-0': !hasSubmitted,
                })}
              >
                <ResultMessageBox
                  messageBody={success ? successMessageBody : errorMessageBody}
                  messageTitle={success ? successMessageTitle : errorMessageTitle}
                  success={success}
                  visible={hasSubmitted}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Grid>
  )
}
